'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import useSWR from 'swr';
import { fetchJson, fetchJsonWithQuery } from 'lib/fetch-json';

export type TimeSlotsFilterType = {
    date: string;
    location_code: string;
    covid19?: boolean;
    omit_appointment_code?: string;
};

export type LocationsFilterType = {
    covid19?: boolean;
};

export type LocationType = {
    id?: number;
    address?: {
        can_be_delivery_address?: boolean;
        street_number?: string;
        county: { name: string; code: string };
        city?: {
            name?: string;
            lng?: string;
            lat?: string;
            county?: { name?: string; code?: string };
        };
        street?: {
            name?: string;
            street_type?: {
                name?: string;
                abbr?: string;
            };
            city?: {
                name?: string;
                lng: string;
                lat: string;
                county?: { name?: string; code: string };
            };
        };
    };
    working_hours?: {
        [key: string]: { start_time?: string; end_time?: string };
    };
    covid19_working_hours?: {
        [key: string]: { start_time?: string; end_time?: string };
    };
    branch_name: string;
    name: string;
    code: string;
};

export type LocationByCounty = {
    name?: string;
    code?: string;
    locations?: LocationType[];
};

export type AppointmentType = {
    code?: string;
    date?: string;
    time_slot?: string;
    state_code?: string;
    location_code?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    notes?: string;
    email?: string;
    email_confirm?: string;
    covid19_sampling?: boolean;
    terms?: boolean;
    recaptcha?: string;
    locale?: string;
    tests?: string[];
};
export type TimeSlotType = {
    available: boolean;
    time: string;
};

type AppointmentsStateType = {
    locationsLoading: boolean;
    locationsByCounty: LocationByCounty[];
    setLocationsFilter: (filter: LocationsFilterType) => void;
    timeSlotsLoading: boolean;
    timeSlots: TimeSlotType[];
    setTimeSlotsFilter: (filter: TimeSlotsFilterType) => void;
    saveAppointment: (params: AppointmentType) => Promise<void>;
    deleteAppointment: (code: string) => Promise<void>;
};

const AppointmentsStateContext = createContext<AppointmentsStateType>({
    locationsLoading: false,
    locationsByCounty: [],
    setLocationsFilter: () => {},
    timeSlotsLoading: false,
    timeSlots: [],
    setTimeSlotsFilter: () => {},
    saveAppointment: async () => {},
    deleteAppointment: async () => {},
});

export function AppointmentsStateProvider(props: any) {
    const [locationsFilter, setLocationsFilter] = useState<any>(null);
    const [timeSlotsFilter, setTimeSlotsFilter] = useState<any>(null);
    const { data: locationsByCounty, isLoading: locationsByCountyLoading } = useSWR(
        () => (locationsFilter ? [`/api/json/locations/by-county`, locationsFilter] : null),
        fetchJsonWithQuery,
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    );

    const { filter_location_code, ...timeSlotsQueryParams } = timeSlotsFilter || {};

    const { data: timeSlots, isLoading: timeSlotsLoading } = useSWR(
        () =>
            timeSlotsQueryParams?.filter_date && filter_location_code
                ? [`/api/json/locations/${filter_location_code}/time-slots`, timeSlotsQueryParams]
                : null,
        fetchJsonWithQuery,
        {
            revalidateIfStale: true,
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
        },
    );

    const value = useMemo<AppointmentsStateType>(() => {
        return {
            locationsLoading: locationsByCountyLoading,
            locationsByCounty: locationsByCounty || [],
            setLocationsFilter: (filter: LocationsFilterType) => {
                setLocationsFilter({
                    covid19: filter?.covid19 ? 1 : undefined,
                });
            },
            timeSlotsLoading: timeSlotsLoading,
            timeSlots: timeSlots || [],
            setTimeSlotsFilter: (filter: TimeSlotsFilterType) => {
                setTimeSlotsFilter({
                    filter_date: filter.date,
                    filter_location_code: filter.location_code,
                    filter_covid19_sampling: filter.covid19 ? '1' : undefined,
                    filter_omit_appointment_code: filter.omit_appointment_code || undefined,
                });
            },
            saveAppointment: async (params: AppointmentType) => {
                return fetchJson(`/json/locations/appointments${params.code ? `/${params.code}` : ''}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ data: params }),
                });
            },
            deleteAppointment: async (code: string) => {
                return fetchJson(`/json/locations/appointments/${code}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                });
            },
        };
    }, [locationsByCounty, timeSlots, locationsByCountyLoading, timeSlotsLoading]);

    return <AppointmentsStateContext.Provider value={value} {...props} />;
}

// a hook which we are going to use whenever we need data from `AppointmentsStateProvider`
export default function useAppointmentsState() {
    const context = useContext(AppointmentsStateContext);

    if (!context) {
        throw new Error('You need to wrap AppointmentsStateProvider.');
    }

    return context;
}
